import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X, ArrowRight, MessageSquarePlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { can } from '../RequirePermission';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('dentalToken')}` });

const hotItems = (u) => {
  const rows = [{ metric: 'patients', label: 'Active patients', ...u.patients }, ...(u.metrics || [])];
  return rows.filter((r) => !r.unlimited && r.pct_used != null && r.pct_used >= 80).sort((a, b) => b.pct_used - a.pct_used);
};

export const UsageAlertBanner = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [period, setPeriod] = useState('');
  const [hidden, setHidden] = useState(false);
  const [textPack, setTextPack] = useState(null);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/billing/usage`, { headers: authHeader() })
      .then(({ data }) => {
        const hot = hotItems(data);
        const level = hot.some((r) => r.pct_used >= 100) ? 100 : 80;
        setPeriod(`${data.period}-${level}`);
        setHidden(sessionStorage.getItem('usageAlertDismissed') === `${data.period}-${level}`);
        setItems(hot);
        if (hot.some((r) => r.metric === 'sms') && can(user, 'billing')) {
          axios.get(`${API}/api/billing/patient-packs`, { headers: authHeader() })
            .then(({ data: pk }) => setTextPack((pk.packs || []).find((p) => p.pack_id === 'texts_1k' && !p.owned) || null))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [user]);

  const buyTexts = async () => {
    setBuying(true);
    try {
      const res = await axios.post(`${API}/api/billing/checkout`, { kind: 'add_pack', pack_id: 'texts_1k', origin_url: window.location.origin }, { headers: authHeader() });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout');
      setBuying(false);
    }
  };

  if (!items.length || hidden) return null;
  const over = items[0].pct_used >= 100;
  const dismiss = () => { sessionStorage.setItem('usageAlertDismissed', period); setHidden(true); };

  return (
    <div
      className={`mb-4 rounded-xl border px-4 py-3 flex items-center gap-3 ${over ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}
      data-testid="usage-alert-banner"
    >
      <AlertTriangle className={`w-5 h-5 shrink-0 ${over ? 'text-red-600' : 'text-amber-600'}`} />
      <div className="flex-1 min-w-0 text-sm">
        <span className={`font-semibold ${over ? 'text-red-900' : 'text-amber-900'}`}>
          {over ? 'Monthly plan limit reached: ' : 'Approaching your monthly plan limit: '}
        </span>
        <span className="text-gray-700" data-testid="usage-alert-items">
          {items.map((r) => `${r.label} ${Math.round(r.pct_used)}%`).join(' · ')}
        </span>
        <span className="text-gray-500"> {over ? '— sending pauses at 120%.' : '— resets on the 1st.'}</span>
      </div>
      {textPack && (
        <button
          onClick={buyTexts}
          disabled={buying}
          className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-60"
          data-testid="usage-alert-buy-texts"
        >
          {buying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquarePlus className="w-3.5 h-3.5" />}
          +1,000 texts · ${textPack.monthly_price}/mo
        </button>
      )}
      {can(user, 'billing') ? (
        <button
          onClick={() => navigate('/plans-billing')}
          className={`shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md text-white transition-colors ${over ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}
          data-testid="usage-alert-view-plan"
        >
          View plan <ArrowRight className="w-3.5 h-3.5" />
        </button>
      ) : (
        <span className="shrink-0 text-xs text-gray-600">Let your office manager know</span>
      )}
      <button onClick={dismiss} className="shrink-0 p-1 rounded hover:bg-black/5 text-gray-500" aria-label="Dismiss" data-testid="usage-alert-dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
